import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "../../lib/api";

/**
 * PortfolioCarousel — 02 · Portafolio (OddlyMade edition).
 *
 * Layout editorial de revista:
 *   • Frame simétrico con 4 thumbnails flotantes en las esquinas.
 *   • Título grande centrado (Cormorant) por categoría.
 *   • Imagen hero grande al centro con Ken Burns + crossfade.
 *   • Counter y controles debajo de la imagen.
 *   • Body copy centrado bajo todo el frame.
 *
 * Los thumbs previsualizan las siguientes imágenes del set; al hacer click
 * saltan a esa imagen. Auto-rotación 4.8 s con pausa en hover.
 */

// ─── Copy por categoría ────────────────────────────────────────
const CATEGORIES = [
  {
    key: "todo",
    label: "Todo",
    subtitle: "El servicio al centro de todo.",
    body:
      "El servicio al cliente es muy importante para nosotros porque sabemos " +
      "que tienes en mente no solamente verte hermosa, sino sentirte especial.",
  },
  {
    key: "novias",
    label: "Novias",
    subtitle: "El día que soñaste, la experiencia que mereces.",
    body:
      "Tu boda merece mucho más que un maquillaje bonito. Merece sentirte escuchada, " +
      "acompañada y segura de que cada detalle está en manos de profesionales.\n\n" +
      "En Makeup Ab ponemos nuestra experiencia, conocimiento y pasión al servicio " +
      "de una sola misión: que llegues a tu gran día sintiéndote tú, pero en tu " +
      "versión más especial.\n\n" +
      "Porque no solo queremos maquillarte para tu boda. Queremos ser parte de un " +
      "recuerdo que llevarás contigo para siempre.",
  },
  {
    key: "sociales",
    label: "Sociales",
    subtitle: "Tu momento. Tu esencia. Tu mejor versión.",
    body:
      "Cada mujer tiene una historia, un estilo y una belleza diferente. Por eso " +
      "creemos que el maquillaje no debe transformarte, sino ayudarte a resaltar " +
      "aquello que te hace única.\n\n" +
      "Ponemos técnica, experiencia y creatividad en cada servicio para diseñar un " +
      "look que se adapte a ti, a tu ocasión y a lo que quieres transmitir.\n\n" +
      "Cuando te ves como imaginabas y te sientes como soñabas, la confianza también " +
      "se vuelve parte del look.",
  },
  {
    key: "clases",
    label: "Clases",
    subtitle: "Compartir lo que sabemos también es parte de nuestra pasión.",
    body:
      "Creemos que el conocimiento tiene más valor cuando se comparte. Por eso " +
      "nuestras clases están diseñadas para enseñarte mucho más que técnicas: " +
      "queremos darte herramientas, criterio y seguridad para que desarrolles tu " +
      "propio estilo y lleves tus habilidades al siguiente nivel.\n\n" +
      "En cada clase compartimos nuestra experiencia y todo aquello que hemos " +
      "aprendido a través de los años en el mundo profesional.\n\n" +
      "No se trata solamente de aprender a maquillar. Se trata de aprender a mirar, " +
      "comprender y crear.",
  },
  {
    key: "servicio",
    label: "Nuestro Servicio",
    subtitle: "Todo lo que sabemos, todo lo que tenemos, para hacerte sentir increíble.",
    body:
      "En Makeup Ab entendemos que un servicio de belleza va mucho más allá del " +
      "resultado final. Está en la atención, en escuchar lo que necesitas, en cuidar " +
      "cada detalle y en poner nuestra experiencia al servicio de tu momento.\n\n" +
      "Trabajamos con preparación, técnica, productos profesionales y, sobre todo, " +
      "con una enorme pasión por lo que hacemos.\n\n" +
      "Un buen servicio no termina cuando dejamos las brochas. Termina cuando tú te " +
      "vas feliz.",
  },
];

const AUTO_ROTATE_MS = 4800;

// ─── Fallback si la categoría no tiene fotos ────────────────
const FALLBACK_BY_CATEGORY = {
  todo: [],
  novias: [
    "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1503236823255-94609f598e71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  ],
  sociales: [
    "https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1457972729786-0411a3b2b626?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  ],
  clases: [
    "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1513270036787-84ce4a1e0aad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  ],
  servicio: [
    "https://images.unsplash.com/photo-1557924760-b14a6d4a4ef3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1613552465135-e5bdaada10bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  ],
};

// ─── Component ──────────────────────────────────────────────
export default function PortfolioCarousel() {
  const [allItems, setAllItems] = useState([]);
  const [activeCat, setActiveCat] = useState("todo");
  const [slideIndex, setSlideIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => setAllItems(r.data || []))
      .catch(() => setAllItems([]));
  }, []);

  const slides = useMemo(() => {
    let list;
    if (activeCat === "todo") {
      list = allItems.map((it) => it.image_url);
    } else {
      list = allItems.filter((it) => it.category === activeCat).map((it) => it.image_url);
    }
    if (!list || list.length === 0) {
      list = FALLBACK_BY_CATEGORY[activeCat] || [];
    }
    if (activeCat === "todo" && list.length > 8) list = list.slice(0, 8);
    return list;
  }, [allItems, activeCat]);

  useEffect(() => setSlideIndex(0), [activeCat]);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    intervalRef.current = setInterval(() => {
      setSlideIndex((i) => (i + 1) % slides.length);
    }, AUTO_ROTATE_MS);
    return () => clearInterval(intervalRef.current);
  }, [paused, slides.length]);

  const goPrev = useCallback(() => {
    setSlideIndex((i) => (i - 1 + slides.length) % Math.max(slides.length, 1));
  }, [slides.length]);
  const goNext = useCallback(() => {
    setSlideIndex((i) => (i + 1) % Math.max(slides.length, 1));
  }, [slides.length]);
  const jumpTo = useCallback((idx) => setSlideIndex(idx), []);

  // ─── Compute thumbnail slots (next 4 unique slides, skipping current) ──
  const thumbSlots = useMemo(() => {
    if (slides.length < 2) return [null, null, null, null];
    const result = [];
    for (let offset = 1; offset <= 4; offset += 1) {
      if (offset < slides.length) {
        result.push((slideIndex + offset) % slides.length);
      } else {
        result.push(null);
      }
    }
    return result;
  }, [slides.length, slideIndex]);

  const active = CATEGORIES.find((c) => c.key === activeCat) || CATEGORIES[0];
  const hasSlides = slides.length > 0;
  const counterCurrent = String(Math.min(slideIndex + 1, slides.length || 1)).padStart(2, "0");
  const counterTotal = String(Math.max(slides.length, 1)).padStart(2, "0");

  return (
    <section
      id="portfolio"
      className="bg-[#F4F0EC] py-24 sm:py-32"
      data-testid="portfolio-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        {/* ── Header ── */}
        <div className="mb-12 sm:mb-16">
          <p className="overline text-[#8B6D5E] mb-4">— 02 · Portafolio</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1]">
            Trabajo <em className="text-[#8B6D5E]">seleccionado</em>.
          </h2>
        </div>

        {/* ── Category tabs ── */}
        <nav
          className="flex flex-wrap gap-x-8 gap-y-3 border-b border-[#1A1A1A]/15 mb-14 sm:mb-20 pb-1"
          data-testid="portfolio-cats"
          role="tablist"
        >
          {CATEGORIES.map((c) => {
            const isActive = c.key === activeCat;
            return (
              <button
                key={c.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveCat(c.key)}
                className={`relative pb-3 overline transition-colors ${
                  isActive ? "text-[#1A1A1A]" : "text-[#5C5C5C] hover:text-[#1A1A1A]"
                }`}
                data-testid={`portfolio-cat-${c.key}`}
              >
                {c.label}
                {isActive && (
                  <span className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-[#1A1A1A]" />
                )}
              </button>
            );
          })}
        </nav>

        {/* ── Editorial frame ── */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="portfolio-frame"
        >
          {/* Desktop layout: [thumbs col] [center] [thumbs col] */}
          <div className="hidden lg:grid grid-cols-[150px_1fr_150px] gap-12 items-stretch">
            {/* Left column of thumbs (TL, BL) */}
            <div className="flex flex-col justify-between gap-8" data-testid="portfolio-thumbs-left">
              <CornerThumb
                slotKey="tl"
                idx={thumbSlots[0]}
                src={thumbSlots[0] != null ? slides[thumbSlots[0]] : null}
                onClick={() => thumbSlots[0] != null && jumpTo(thumbSlots[0])}
                label={active.label}
              />
              <CornerThumb
                slotKey="bl"
                idx={thumbSlots[2]}
                src={thumbSlots[2] != null ? slides[thumbSlots[2]] : null}
                onClick={() => thumbSlots[2] != null && jumpTo(thumbSlots[2])}
                label={active.label}
              />
            </div>

            {/* Center column */}
            <CenterStage
              active={active}
              slides={slides}
              slideIndex={slideIndex}
              hasSlides={hasSlides}
              counterCurrent={counterCurrent}
              counterTotal={counterTotal}
              goPrev={goPrev}
              goNext={goNext}
            />

            {/* Right column of thumbs (TR, BR) */}
            <div
              className="flex flex-col justify-between gap-8"
              data-testid="portfolio-thumbs-right"
            >
              <CornerThumb
                slotKey="tr"
                idx={thumbSlots[1]}
                src={thumbSlots[1] != null ? slides[thumbSlots[1]] : null}
                onClick={() => thumbSlots[1] != null && jumpTo(thumbSlots[1])}
                label={active.label}
              />
              <CornerThumb
                slotKey="br"
                idx={thumbSlots[3]}
                src={thumbSlots[3] != null ? slides[thumbSlots[3]] : null}
                onClick={() => thumbSlots[3] != null && jumpTo(thumbSlots[3])}
                label={active.label}
              />
            </div>
          </div>

          {/* Mobile layout: centered stage only, thumbs strip below */}
          <div className="lg:hidden">
            <CenterStage
              active={active}
              slides={slides}
              slideIndex={slideIndex}
              hasSlides={hasSlides}
              counterCurrent={counterCurrent}
              counterTotal={counterTotal}
              goPrev={goPrev}
              goNext={goNext}
              compact
            />

            {/* Thumb strip */}
            {thumbSlots.some((t) => t != null) && (
              <div
                className="mt-8 flex gap-3 justify-center flex-wrap"
                data-testid="portfolio-thumbs-mobile"
              >
                {thumbSlots.map((idx, k) => {
                  if (idx == null) return null;
                  return (
                    <button
                      key={k}
                      onClick={() => jumpTo(idx)}
                      className="w-14 h-16 overflow-hidden bg-[#1A1A1A]/10 relative group"
                      aria-label={`Ir a la imagen ${idx + 1}`}
                    >
                      <img
                        src={slides[idx]}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Body copy centered below the frame ── */}
        <div
          key={active.key}
          className="mt-16 sm:mt-20 max-w-2xl mx-auto text-center animate-[fadeslide_.6s_ease]"
          data-testid="portfolio-body"
        >
          <div className="space-y-4 text-[#3D3D3D] text-base sm:text-lg leading-[1.85]">
            {active.body.split("\n\n").map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeslide {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes kenburns {
          from { transform: scale(1.03); }
          to   { transform: scale(1.10); }
        }
        .kenburns { animation: kenburns ${AUTO_ROTATE_MS + 900}ms ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .kenburns { animation: none !important; }
        }
      `}</style>
    </section>
  );
}

// ─── Center stage: title + big image + counter/controls ─────
function CenterStage({
  active,
  slides,
  slideIndex,
  hasSlides,
  counterCurrent,
  counterTotal,
  goPrev,
  goNext,
  compact = false,
}) {
  return (
    <div
      className="flex flex-col items-center max-w-2xl mx-auto w-full"
      data-testid="portfolio-stage"
    >
      {/* Title block */}
      <div
        key={active.key}
        className="text-center animate-[fadeslide_.6s_ease] mb-8"
      >
        <p className="overline text-[#8B6D5E] mb-4">— {active.label}</p>
        <h3
          className={`font-serif text-[#1A1A1A] tracking-tight leading-[1.04] ${
            compact ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl lg:text-6xl"
          }`}
          data-testid="portfolio-subtitle"
        >
          {active.subtitle}
        </h3>
      </div>

      {/* Big centered image */}
      <div
        className={`relative w-full ${
          compact ? "aspect-[4/5] max-w-md" : "aspect-[4/5] max-w-xl"
        } overflow-hidden bg-[#1A1A1A]/5`}
        data-testid="portfolio-carousel"
      >
        {!hasSlides ? (
          <div className="absolute inset-0 flex items-center justify-center text-[#5C5C5C] overline">
            — Sin fotos por ahora —
          </div>
        ) : (
          slides.map((src, i) => {
            const isActiveSlide = i === slideIndex;
            return (
              <img
                key={`${active.key}-${i}`}
                src={src}
                alt={`${active.label} ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-out ${
                  isActiveSlide ? "opacity-100 kenburns" : "opacity-0"
                }`}
                data-testid={`portfolio-slide-${i}`}
                aria-hidden={!isActiveSlide}
              />
            );
          })
        )}
      </div>

      {/* Counter + controls */}
      <div
        className="mt-6 w-full flex items-center gap-4 sm:gap-6"
        data-testid="portfolio-controls"
      >
        <div
          className="overline text-[#5C5C5C] tabular-nums flex-none"
          data-testid="portfolio-counter"
        >
          {counterCurrent}
          <span className="text-[#8B6D5E]"> / </span>
          {counterTotal}
        </div>
        <div className="flex-1 h-[1px] bg-[#1A1A1A]/20" />
        <div className="flex items-center gap-2 flex-none">
          <button
            onClick={goPrev}
            disabled={slides.length <= 1}
            aria-label="Anterior"
            className="w-10 h-10 flex items-center justify-center border border-[#1A1A1A]/30 hover:border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAFAFA] transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-1 focus-visible:ring-[#1A1A1A]"
            data-testid="portfolio-prev"
          >
            <span aria-hidden className="inline-block leading-none">←</span>
          </button>
          <button
            onClick={goNext}
            disabled={slides.length <= 1}
            aria-label="Siguiente"
            className="w-10 h-10 flex items-center justify-center border border-[#1A1A1A]/30 hover:border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAFAFA] transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-1 focus-visible:ring-[#1A1A1A]"
            data-testid="portfolio-next"
          >
            <span aria-hidden className="inline-block leading-none">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Corner thumb ────────────────────────────────────────────
function CornerThumb({ slotKey, idx, src, onClick, label }) {
  if (idx == null || !src) {
    return <div className="aspect-[4/5]" aria-hidden />;
  }
  return (
    <button
      onClick={onClick}
      className="group relative aspect-[4/5] overflow-hidden bg-[#1A1A1A]/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#1A1A1A]"
      data-testid={`portfolio-thumb-${slotKey}`}
      aria-label={`${label} — imagen ${idx + 1}`}
    >
      <img
        src={src}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08]"
      />
      <span className="absolute inset-0 bg-[#1A1A1A]/0 group-hover:bg-[#1A1A1A]/15 transition-colors" />
      <span className="absolute bottom-2 left-2 overline text-[10px] text-white/95 mix-blend-difference tabular-nums">
        {String(idx + 1).padStart(2, "0")}
      </span>
    </button>
  );
}
